import { useCallback, useMemo, useState } from 'react';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, MarketplaceFiltersBiEvent } from '@wix/da-bi/pkg/events';
import {
  type ShopFilters,
  SHOP_FILTER_DEFAULTS,
  NON_FILTER_PARAMS,
} from '../../../../../types/shop';

const NON_FILTER_PARAM_KEYS = Object.keys(NON_FILTER_PARAMS);

interface UseShopFilterStateReturn {
  filters: ShopFilters;
  filterCount: number;
  updateFilters: (newFilters?: ShopFilters) => void;
  reloadShopWithFilters: (newFilters?: ShopFilters) => void;
  resetFilters: () => void;
}

export default function useShopFilterState(
  initialFilters = {} as ShopFilters
): UseShopFilterStateReturn {
  const logBiEvent = useBiLogger();

  const [filters, setFilters] = useState<ShopFilters>({
    ...SHOP_FILTER_DEFAULTS,
    ...initialFilters,
  });

  const updateFilters = (newFilters: ShopFilters): void => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  const reloadShopWithFilters = useCallback(
    (newFilters = {} as ShopFilters): void => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);

      const truthyFilters = filterOutFalsyFields(updatedFilters);
      logBiEvent(
        BiData<MarketplaceFiltersBiEvent>({
          evid: 377,
          ...truthyFilters,
        })
      );

      if (typeof window === 'undefined') {
        return;
      }

      const { search } = window.location;
      const searchParams = new URLSearchParams(search);
      const nonFilterParams = NON_FILTER_PARAM_KEYS.reduce((obj, key) => {
        if (searchParams.get(key)) {
          obj[key] = searchParams.get(key);
        }
        return obj;
      }, {});
      const allParams = {
        ...truthyFilters,
        ...nonFilterParams,
      };
      let newQuerystring = new URLSearchParams(allParams).toString();
      // backend expects 1/0 instead of true/false
      newQuerystring = newQuerystring.replace(/true/g, '1');

      window.location.assign(
        Object.keys(allParams).length
          ? `${window.location.pathname}?${newQuerystring}`
          : `${window.location.pathname}`
      );
    },
    [filters, logBiEvent]
  );

  const filterCount = useMemo(() => {
    const nonDefaultingFilters = filterOutFalsyFields(filters);
    const rawFilterCount = Object.keys(nonDefaultingFilters).length;

    // to the user this isn't a separate filter from min_price, just 'price'
    const hasMaxPrice = nonDefaultingFilters.hasOwnProperty('max_price');
    return hasMaxPrice ? rawFilterCount - 1 : rawFilterCount;
  }, [filters]);

  return {
    filters,
    filterCount,
    updateFilters,
    reloadShopWithFilters,
    resetFilters: (): void => setFilters(SHOP_FILTER_DEFAULTS),
  };
}

function filterOutFalsyFields(obj: Record<string, any>): Record<string, any> {
  const filtered = {} as ShopFilters;
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value) {
      filtered[key] = value;
    }
  }

  // special case if status is 'open'
  if (filtered.status === SHOP_FILTER_DEFAULTS.status) {
    // just check if a filter is set to a default
    // TODO: generalize
    delete filtered.status;
  }

  return filtered;
}
